import * as React from 'react';
import classNames from 'classnames';
import get from 'lodash/get';
import map from 'lodash/map';
import isUndefined from 'lodash/isUndefined';
import { injectIntl } from 'react-intl';
import type { IntlShape } from 'react-intl';
import Label from 'box-ui-elements/es/components/label/Label';
import Tooltip from 'box-ui-elements/es/components/tooltip/Tooltip';
import MultiSelectFieldPrimitive from 'box-ui-elements/es/components/select-field/MultiSelectField';
import SingleSelectFieldPrimitive from 'box-ui-elements/es/components/select-field/SingleSelectField';
import messages from './messages';
import css from './select-field.module.scss';
import useId from '../../utils/useId';
import type { SelectFieldProps } from './select-field';

type Props = SelectFieldProps & {
  intl: IntlShape;
};

const BuieSelectField = React.memo<Props>((props: Props) => {
  const {
    className = '',
    label,
    description,
    error,
    infoText,
    isRequired,
    isDisabled,
    isMultiple,
    onChange,
    options,
    selectedValues,
    isInTwoColumn,
    maximumSelections,
    intl,
  } = props;

  const [isHovering, setIsHovering] = React.useState(false);

  const mouseProps = {
    onMouseEnter: () => setIsHovering(true),
    onMouseLeave: () => setIsHovering(false),
  } as const;

  const isTooltipShown = !!infoText && isHovering;

  const hasError = !!error;
  const classes = classNames(className, 'select-field-container', {
    [css.withTooltip]: isTooltipShown,
    [css.withoutTooltip]: !isTooltipShown,
    [css.halfColumnWidth]: isInTwoColumn,
  });

  const errorMessageID = useId('errorMessage');
  const descriptionID = useId('description');
  const ariaAttrs = {
    'aria-invalid': hasError,
    'aria-required': isRequired,
    'aria-errormessage': errorMessageID,
  } as const;

  const optionObjects = map(options, option => ({
    displayText: option,
    value: option,
  }));

  const placeholder =
    isMultiple ||
    (typeof maximumSelections === 'number' &&
      (maximumSelections === 0 || maximumSelections > 1))
      ? intl.formatMessage(messages.multiSelectFieldPlaceholderMessage)
      : intl.formatMessage(messages.singleSelectFieldPlaceholderMessage);

  if (!isMultiple) {
    optionObjects.unshift({
      displayText: placeholder,
      // @ts-expect-error [MIGRATION-SUPPRESSION]: TS2322
      value: undefined,
    });
  }

  const handleOnChange = (
    selectedOptions: Array<{
      displayText: string;
      value?: string;
    }>,
  ) => {
    const filteredSelectedOptions = selectedOptions.filter(
      option => !isUndefined(option.value),
    );
    onChange(filteredSelectedOptions);
  };

  const selectButtonId = useId('select-');
  const selectButtonProps = {
    id: selectButtonId,
    'aria-describedby': description ? descriptionID : undefined,
  } as const;

  return (
    <div className={classes}>
      <Label labelElProps={{ htmlFor: selectButtonId }} text={label} />

      {!!description && (
        <div className="select-field-description" id={descriptionID}>
          {description}
        </div>
      )}

      <Tooltip
        className="field-tooltip"
        isShown={isTooltipShown}
        position="middle-right"
        text={infoText}
      >
        <div {...mouseProps}>
          {isMultiple ? (
            <MultiSelectFieldPrimitive
              buttonProps={selectButtonProps}
              // @ts-expect-error [MIGRATION-SUPPRESSION]: TS2339
              className={classNames(css.selectField, {
                [css.invalidSelectField]: hasError,
              })}
              error={error}
              isDisabled={isDisabled}
              onChange={(
                selectedOptions: Array<{
                  displayText: string;
                  value?: string;
                }>,
              ) => {
                handleOnChange(selectedOptions);
              }}
              options={optionObjects}
              placeholder={placeholder}
              selectedValues={selectedValues}
              {...ariaAttrs}
            />
          ) : (
            <SingleSelectFieldPrimitive
              buttonProps={selectButtonProps}
              // @ts-expect-error [MIGRATION-SUPPRESSION]: TS2339
              className={classNames(css.selectField, {
                [css.invalidSelectField]: hasError,
              })}
              error={error}
              isDisabled={isDisabled}
              onChange={(selectedOption: {
                displayText: string;
                value?: string;
              }) => {
                handleOnChange([selectedOption]);
              }}
              options={optionObjects}
              placeholder={placeholder}
              selectedValue={get(selectedValues, '0', null)}
              {...ariaAttrs}
            />
          )}
        </div>
      </Tooltip>
      <span className="accessibility-hidden" id={errorMessageID} role="alert">
        {error}
      </span>
    </div>
  );
});

BuieSelectField.displayName = 'BuieSelectField';

export type BuieSelectFieldProps = Props;
export { BuieSelectField };
type WrappedProps = Omit<Props, 'intl'>;
const Wrapped = injectIntl(BuieSelectField);
export default Wrapped as React.ComponentType<WrappedProps>;
